import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    FaGraduationCap,
    FaBriefcase,
    FaCalendarAlt,
    FaClipboardList,
    FaArrowRight,
    FaPlayCircle,
    FaChartLine,
    FaUserCheck,
    FaClock,
    FaPercentage,
    FaFileInvoiceDollar
} from 'react-icons/fa';
import WaveChart from '../../SuperAdmin/shared/WaveChart';
import Loader from '../../shared/Loader';
import { API_URL } from '../../../config';
import './StudentDashboardOverview.css';

const Overview = () => {
    const [stats, setStats] = useState({
        enrolledCourses: 0,
        appliedJobs: 0,
        pendingAssessments: 0,
        upcomingInterviews: 0
    });
    const [performance, setPerformance] = useState({
        courses: [],
        overallProgress: 0,
        overallAttendance: 0
    });
    const [invoices, setInvoices] = useState([]);
    const [upcomingClasses, setUpcomingClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const handleJoinSession = async (session) => {
        try {
            const token = localStorage.getItem('token');
            // Call join endpoint to mark attendance/increment count
            const res = await axios.post(`/api/student/live-classes/${session._id}/join`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // Open external meeting URL
            window.open(res.data.externalMeetingUrl, '_blank');
        } catch (err) {
            console.error("Join session error:", err);
            // Fallback: open URL anyway if API fails
            window.open(session.externalMeetingUrl, '_blank');
        }
    };

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const token = localStorage.getItem('token');
                const headers = { Authorization: `Bearer ${token}` };

                const [statsRes, classesRes, perfRes, invRes] = await Promise.all([
                    axios.get('/api/student/dashboard-stats', { headers }),
                    axios.get('/api/student/upcoming-classes', { headers }),
                    axios.get('/api/student/performance-analytics', { headers }),
                    axios.get('/api/student/invoices', { headers })
                ]);

                setStats(statsRes.data);
                setUpcomingClasses(classesRes.data);
                setPerformance(perfRes.data);
                setInvoices(invRes.data);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching student dashboard data:", err);
                setLoading(false);
            }
        };
        fetchDashboardData();
    }, []);

    if (loading) return <Loader />;

    return (
        <div className="student-overview fade-in">
            <div className="dashboard-header">
                <h1 className="page-title">Dashboard Intelligence</h1>
                <p className="page-subtitle">Unified metrics and upcoming milestones for your professional trajectory.</p>
            </div>

            {/* KPI Grid */}
            <div className="kpi-grid">
                <div className="kpi-card" onClick={() => navigate('/student-dashboard/courses')}>
                    <div className="kpi-header">
                        <span className="kpi-title">Active Courses</span>
                        <div className="kpi-icon"><FaGraduationCap /></div>
                    </div>
                    <h2 className="kpi-value">{stats.enrolledCourses}</h2>
                    <div className="kpi-footer">
                        <span className="highlight">LMS Portal</span> active
                    </div>
                </div>

                <div className="kpi-card" onClick={() => navigate('/student-dashboard/jobs')}>
                    <div className="kpi-header">
                        <span className="kpi-title">Applied Jobs</span>
                        <div className="kpi-icon"><FaBriefcase /></div>
                    </div>
                    <h2 className="kpi-value">{stats.appliedJobs}</h2>
                    <div className="kpi-footer">
                        <span className="highlight">Candidate DB</span> synced
                    </div>
                </div>

                <div className="kpi-card" onClick={() => navigate('/student-dashboard/assessments')}>
                    <div className="kpi-header">
                        <span className="kpi-title">Pending Tasks</span>
                        <div className="kpi-icon"><FaClipboardList /></div>
                    </div>
                    <h2 className="kpi-value">{stats.pendingAssessments}</h2>
                    <div className="kpi-footer">
                        Assignments & Exams
                    </div>
                </div>

                <div className="kpi-card" onClick={() => navigate('/student-dashboard/applications')}>
                    <div className="kpi-header">
                        <span className="kpi-title">Interviews</span>
                        <div className="kpi-icon"><FaCalendarAlt /></div>
                    </div>
                    <h2 className="kpi-value">{stats.upcomingInterviews}</h2>
                    <div className="kpi-footer">
                        Scheduled this month
                    </div>
                </div>

            </div>

            {/* Analytics Row */}
            <div className="analytics-row">
                <div className="content-card analytics-card">
                    <div className="card-header">
                        <h3 className="card-title"><FaChartLine /> My Academic Progress</h3>
                        <div className="overall-badge gold">
                            Overall: {performance.overallProgress}%
                        </div>
                    </div>
                    <div className="chart-container">
                        <WaveChart
                            data={performance.courses.map(c => ({ name: c.courseTitle, progress: c.progress }))}
                            xKey="name"
                            yKey="progress"
                            color="#D4AF37"
                            height={250}
                        />
                    </div>
                </div>

                <div className="content-card analytics-card">
                    <div className="card-header">
                        <h3 className="card-title"><FaUserCheck /> Attendance Insights</h3>
                        <div className="overall-badge blue">
                            Avg: {performance.overallAttendance}%
                        </div>
                    </div>
                    <div className="attendance-scroll">
                        {performance.courses.length > 0 ? (
                            performance.courses.map((c, idx) => (
                                <div key={idx} className="attendance-item">
                                    <div className="attendance-main">
                                        <div className="tutor-mini-avatar">
                                            {c.tutorImage ? (
                                                <img src={`${API_URL}${c.tutorImage}`} alt={c.tutorName} />
                                            ) : c.tutorName.charAt(0)}
                                        </div>
                                        <div className="attendance-info">
                                            <span className="course-name">{c.courseTitle}</span>
                                            <span className="tutor-name">Tutor: {c.tutorName}</span>
                                        </div>
                                    </div>
                                    <div className="attendance-stats">
                                        <div className="stat-pill">
                                            <FaClock /> {c.presentDays}/{c.totalSessions} Sessions
                                        </div>
                                        <div className="percentage-circle" style={{ '--percent': c.attendance }}>
                                            <span>{c.attendance}%</span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="empty-attendance">
                                <FaPercentage /> No session data available yet.
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="overview-grid">
                <div className="content-card">
                    <div className="card-header">
                        <h3 className="card-title">Upcoming Live Sessions</h3>
                        <span className="view-all" onClick={() => navigate('/student-dashboard/courses')}>
                            View Schedule <FaArrowRight />
                        </span>
                    </div>

                    <div className="class-list">
                        {upcomingClasses.length > 0 ? (
                            upcomingClasses.map((cls) => (
                                <div key={cls._id} className="class-item">
                                    <div className="class-date">
                                        <span className="day">{new Date(cls.scheduledDate).getDate()}</span>
                                        <span className="month">
                                            {new Date(cls.scheduledDate).toLocaleString('default', { month: 'short' })}
                                        </span>
                                    </div>
                                    <div className="class-info">
                                        <h4>{cls.topic}</h4>
                                        <p>{cls.courseId?.title} • {cls.startTime}</p>
                                    </div>
                                    <button
                                        className="join-btn"
                                        onClick={() => handleJoinSession(cls)}
                                    >
                                        Join Now
                                    </button>
                                </div>
                            ))
                        ) : (
                            <p style={{ textAlign: 'center', color: '#64748B', padding: '20px' }}>
                                No live classes scheduled for your courses at the moment.
                            </p>
                        )}
                    </div>
                </div>

                <div className="content-card">
                    <div className="card-header">
                        <h3 className="card-title">Learning Status</h3>
                    </div>
                    <div style={{ padding: '20px 0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span style={{ fontSize: '13px', fontWeight: 600 }}>Sync Completion</span>
                            <span style={{ fontSize: '13px', color: 'var(--primary-gold)', fontWeight: 700 }}>{performance.overallProgress}%</span>
                        </div>
                        <div style={{ height: '8px', background: '#F1F5F9', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${performance.overallProgress}%`, height: '100%', background: 'var(--primary-gold)', transition: 'width 0.5s ease-out' }}></div>
                        </div>
                        <p style={{ fontSize: '12px', color: '#64748B', marginTop: '12px', lineHeight: '1.5' }}>
                            {performance.overallProgress >= 100
                                ? "Course Completed! You're ready for the board exams."
                                : `You are on track. Continue learning to reach 100% completion.`}
                        </p>

                        <button
                            className="primary-btn"
                            style={{ width: '100%', marginTop: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                            onClick={() => navigate('/student-dashboard/courses')}
                        >
                            <FaPlayCircle /> Resume Learning
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Overview;
